import _ from 'underscore';
import {
  ChangeTypeEnum,
  ChangeTypeEnumValue,
  InsertionOrderChangeAmountField,
  InsertionOrderChangeTypeField,
  InsertionOrderColumns,
  InsertionOrderReportColumns,
  MismatchedReasonCode,
  ViewItem,
} from '@bingads-webui-campaign/reports-v4-component';
import { isTotalRow } from './row-helper';

export function isInsertionOrderReport({ reportType, permissions }) {
  return _.get(permissions, ['dynamic', 'InsertionOrderReport'], false) && _.isString(reportType) && (reportType.toLowerCase() === 'insertionorderreport');
}

export function isInsertionOrderReportTeachingBubbleEnabled({ reportType, permissions, isCreatePage }) {
  return isInsertionOrderReport({ reportType, permissions })
    && isCreatePage
    && _.get(permissions, ['dynamic', 'InsertionOrderReportTeachingBubble'], false);
}

export function getColumnsByView({ defaultColumns, viewType }) {
  switch (viewType) {
    case ViewItem.Monthly:
    case ViewItem.Daily:
      return _.chain(InsertionOrderReportColumns[viewType].add)
        .union(defaultColumns)
        .filter(column => !InsertionOrderReportColumns[viewType].remove.includes(column))
        .value();
    default:
      return defaultColumns;
  }
}

export function getSortedColumnByView({ viewType }) {
  switch (viewType) {
    case ViewItem.Monthly:
    case ViewItem.Daily:
      return InsertionOrderReportColumns[viewType].sort;
    default:
      return [];
  }
}

export function getRejectedColumnsByView({
  reportType, restrictedColumnSets, permissions, viewType, isCustomerScope,
}) {
  if (!isInsertionOrderReport({ reportType, permissions })) {
    return new Set();
  }

  const rejectedColumns = new Set();

  restrictedColumnSets.forEach(({ columns, isAllowed }) => {
    if (!isAllowed(permissions, isCustomerScope)) {
      columns.forEach(column => rejectedColumns.add(column));
    }
  });

  switch (viewType) {
    case ViewItem.Monthly:
    case ViewItem.Daily:
      InsertionOrderReportColumns[viewType].remove.forEach(column => rejectedColumns.add(column));
      break;
    default:
  }
  return rejectedColumns;
}

export function getColumnChooserSelectedKey({
  reportType, permissions, columns, rejectedColumns,
}) {
  const selectedKeys = columns || [];
  return isInsertionOrderReport({ reportType, permissions })
    ? _.filter(selectedKeys, c => !rejectedColumns.has(c))
    : selectedKeys;
}

export function isInsertionOrderMonthlyView({
  reportType, permissions, viewType, columns,
}) {
  return isInsertionOrderReport({ reportType, permissions }) &&
    (viewType === ViewItem.Monthly || (_.isArray(columns) && _.contains(columns, 'IOReportMonth')));
}

export function addInsertionOrderIdFilter({ oldFilter, insertionOrderIds }) {
  if (_.isEmpty(insertionOrderIds)) {
    return oldFilter;
  }
  const filterBesidesAnd = [];
  _.chain(oldFilter).omit('$and').forEach((val, key) => { filterBesidesAnd.push({ [key]: val }); });
  return {
    ...oldFilter,
    $and: _.union(oldFilter.$and, filterBesidesAnd, [{ InsertionOrderId: { $in: insertionOrderIds } }]),
  };
}

export function removeInsertionOrderIdFilter({ isInsertionOrderReportEnabled, oldFilter }) {
  if (isInsertionOrderReportEnabled && !_.isEmpty(oldFilter)) {
    if (_.has(oldFilter, InsertionOrderColumns.InsertionOrderId)) {
      return _.omit(oldFilter, InsertionOrderColumns.InsertionOrderId);
    }
    const filterAnd = _.get(oldFilter, '$and');
    if (!_.isEmpty(filterAnd)) {
      return {
        ...oldFilter,
        $and: _.reject(filterAnd, filter => _.has(filter, InsertionOrderColumns.InsertionOrderId)),
      };
    }
  }
  return oldFilter;
}

function isRowWithBudgetChangeDescription(row) {
  const budgetChangeOldValue = _.get(row, ['BudgetChangeOldValue', 'RawValue'], undefined);
  const budgetChangeNewValue = _.get(row, ['BudgetChangeNewValue', 'RawValue'], undefined);
  return budgetChangeOldValue && budgetChangeNewValue && (Number(budgetChangeOldValue) !== 0 || Number(budgetChangeNewValue) !== 0);
}

export function aggragatorInsertionOrderReportData({ rowDefinitions, reportParam, permissions }) {
  if (!isInsertionOrderReport({ reportType: reportParam.reportType, permissions })) {
    return rowDefinitions;
  }

  const [totalRows, normalRows] = _.partition(rowDefinitions, isTotalRow);
  // For daily view:
  // add WithBudgetChangeDescription, SpendAmountValue, AdjustmentAmountValue, BudgetChangedAmountValue to each row
  if (!isInsertionOrderMonthlyView({ reportType: reportParam.reportType, permissions, columns: reportParam.columns })) {
    const normalizedChangeAmountRows = _.map(normalRows, (row) => {
      const WithBudgetChangeDescription = isRowWithBudgetChangeDescription(row);
      const changeType = _.get(row, ['ChangeType', 'Value']);
      if (InsertionOrderChangeAmountField[changeType]) {
        return {
          ...row,
          WithBudgetChangeDescription,
          [InsertionOrderChangeAmountField[changeType]]: _.get(row, 'ChangeAmount'),
        };
      }
      return { ...row, WithBudgetChangeDescription };
    });
    return [normalizedChangeAmountRows, totalRows].flat();
  }

  // For monthly view, aggregated others according to IOReportMonth and InsertionOrderDetails
  const rowsGroupByMonthIODetails = _.groupBy(normalRows, row => `${_.get(row, ['IOReportMonth', 'Value'])}-${_.get(row, ['AccountNumber', 'Value'])}-${_.get(row, ['InsertionOrderId', 'Value'])}`);
  const monthlyViewData = _.mapObject(rowsGroupByMonthIODetails, (rowsItem) => {
    const budgetChangeItem = _.find(rowsItem, item => _.get(item, ['ChangeType', 'Value']) === ChangeTypeEnum.BudgetChange);
    const WithBudgetChangeDescription = isRowWithBudgetChangeDescription(budgetChangeItem);

    // BudgetChangeItem does not have BillingDocumentNumber
    const withBdItem = _.find(rowsItem, item => _.get(item, ['ChangeType', 'Value']) === ChangeTypeEnum.Spend || _.get(item, ['ChangeType', 'Value']) === ChangeTypeEnum.SystemAdjustment);
    let bdValues = {};
    if (withBdItem) {
      bdValues = _.pick(withBdItem, 'BillingDocumentNumber', 'BDAmount', 'MismatchedAmount', 'MismatchedReason');
    }
    const row = {
      ...rowsItem[0],
      ...budgetChangeItem, // To get correct BudgetChangeOldValue and BudgetChangeNewValue
      WithBudgetChangeDescription,
      ...bdValues, // To get correct BillingDocumentNumber, BDAmount, MismatchedAmount and MismatchedReason
    };

    rowsItem.forEach((item) => {
      const changeType = _.get(item, ['ChangeType', 'Value']);
      if (InsertionOrderChangeAmountField[changeType]) {
        row[InsertionOrderChangeTypeField[changeType]] = changeType;
        row[InsertionOrderChangeAmountField[changeType]] = _.get(item, 'ChangeAmount');
      }
    });
    return row;
  });
  return [_.values(monthlyViewData), totalRows].flat();
}

export function getMismatchIconCount({ items, reportParam, permissions }) {
  if (!isInsertionOrderReport({ reportType: reportParam.reportType, permissions })) {
    return -1;
  }
  const itemsWithMismatchReason = _.filter(items, item => _.contains(_.values(MismatchedReasonCode), _.get(item, ['MismatchedReason', 'Value'])));
  return itemsWithMismatchReason.length;
}

export function getInsertionOrderSelectorValue({
  reportType, permissions, allFilters, ioidFromUrl,
}) {
  if (!isInsertionOrderReport({ reportType, permissions })) {
    return [];
  }

  // Use insertionOrderIdFromFilter in saved report (reportAction='edit') and no other filters.
  const insertionOrderIdFromFilter = _.get(allFilters, InsertionOrderColumns.InsertionOrderId)
    ? _.chain(allFilters)
      .get(InsertionOrderColumns.InsertionOrderId)
      .values().first()
      .value()
    : null;

  // Use insertionOrderIdFilterFromAnd in saved report (reportAction='edit') and has other filters, such as ChangeAmount > 100.
  const insertionOrderIdFilterFromAnd = _.get(allFilters, '$and')
    ? _.chain(allFilters)
      .get('$and')
      .find(filter => _.has(filter, InsertionOrderColumns.InsertionOrderId))
      .values()
      .first()
      .values()
      .first()
      .value()
    : null;

  // Use insertionOrderIdFromUrl in default report (reportAction='create') and the url is from the click of RemainingIOBudget cell in Insertion order grid.
  const insertionOrderIdFromUrl = ioidFromUrl ? [ioidFromUrl] : null;

  const rawValue = insertionOrderIdFromFilter || insertionOrderIdFilterFromAnd || insertionOrderIdFromUrl || [];
  return _.isArray(rawValue) ? _.map(rawValue, ioId => Number(ioId)) : [rawValue];
}

export function getInitialInsertionOrderSelectorItems({ allInsertionOrders, insertionOrderIds }) {
  if (_.isEmpty(allInsertionOrders) || _.isEmpty(insertionOrderIds)) {
    return [];
  }
  return _.filter(allInsertionOrders, io => _.contains(insertionOrderIds, io.id));
}

export function shouldDisplayTotalAmount({ filters, changeType }) {
  let changeTypeFilterArray = [];
  if (!_.isEmpty(filters)) {
    if (_.has(filters, 'ChangeType')) {
      changeTypeFilterArray = filters.ChangeType.$in;
    } else if (!_.isEmpty(filters.$and)) {
      changeTypeFilterArray = _.chain(filters.$and).find(item => _.has(item, 'ChangeType')).get('$in').value();
    }
  }
  // If changeTypeFilterArray is empty, return true;
  // If changeTypeFilterArray has value, and it contains the queried changeType, return true;
  // If changeTypeFilterArray has value, and it does not contain the queried changeType, return false.
  return _.isEmpty(changeTypeFilterArray) || _.contains(changeTypeFilterArray, ChangeTypeEnumValue[changeType]);
}
